import { useEffect, useRef, useState } from "react";
import type { UsbDeviceConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { t } from "../lib/uiStrings";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";

/** One small illustration per destination zone in the drag-and-drop decision
 *  (2026-07-25 redesign) — deliberately generic pictograms (a desktop tower,
 *  a personal laptop, a shield+envelope for "report it"), matched to each
 *  option's `key` rather than hardcoded per-option-id so any future usbdevice
 *  decision with the same 3 keys gets a sensible icon for free. */
function UsbTargetIcon({ optKey }: { optKey: string }) {
  if (optKey === "safe") {
    return (
      <svg viewBox="0 0 48 48" width="34" height="34" aria-hidden fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M24 5 39 11v11c0 9-6.5 15-15 17-8.5-2-15-8-15-17V11Z" strokeLinejoin="round" />
        <path d="M17.5 24.5 22 29l9-10" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // both "risky" options in this decision are "plug it into a computer" —
  // differentiate reception-PC (tower + monitor) vs personal laptop by id.
  return (
    <svg viewBox="0 0 48 48" width="34" height="34" aria-hidden fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="7" y="9" width="26" height="18" rx="1.8" strokeLinejoin="round" />
      <path d="M4 32h32" strokeLinecap="round" />
      <rect x="34" y="16" width="9" height="14" rx="1.4" strokeLinejoin="round" />
    </svg>
  );
}
function UsbLaptopIcon() {
  return (
    <svg viewBox="0 0 48 48" width="34" height="34" aria-hidden fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 12h24v16H12Z" strokeLinejoin="round" />
      <path d="M5 33h38l-3 5H8Z" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Episode 2 ("The suspicious delivery") — a labelled USB drive + note,
 * unpacked from the 3D package on the desk (see {@link UsbDeviceConfig}),
 * then (only on the risky path) a follow-up "what now" recovery moment. See
 * {@link UsbDeviceConfig} for the two render modes. Choices never show
 * VERIFY/RISKY spoilers (`showRiskLabel={false}`) — the learner decides
 * blind, exactly like a real inbox/desk moment; the teach beat that follows
 * explains why.
 */
export function UsbDevice({
  config,
  onEnded,
}: {
  config: UsbDeviceConfig;
  onEnded: (key: string, choiceId: string, eventId?: string) => void;
}) {
  const started = useRef(false);
  const [stage, setStage] = useState<"note" | "dnd">("note");
  const [drag, setDrag] = useState<{ x: number; y: number; overId: string | null } | null>(null);
  const dragRef = useRef<typeof drag>(null);
  dragRef.current = drag;
  const zoneRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (config.mode === "decision") audio.ui("alert");
  }, [config]);

  const pick = (opt: UsbDeviceConfig["options"][number]) => {
    // 2026-07-25 feedback: swap the generic click/insert sound for the same
    // correct/incorrect quiz chime every other drag-drop decision game uses
    // (see PrivacySort.tsx) instead of a plain UI click either way.
    const risky = opt.key === "risky" || opt.key === "not_reported";
    if (risky) audio.chimeBad();
    else audio.chimeGood();
    onEnded(opt.key, opt.id, opt.eventId);
  };

  const zoneAt = (x: number, y: number): string | null => {
    for (const opt of config.options) {
      const el = zoneRefs.current[opt.id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return opt.id;
    }
    return null;
  };

  const startUsbDrag = (clientX: number, clientY: number) => {
    audio.click();
    setDrag({ x: clientX, y: clientY, overId: null });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const overId = zoneAt(e.clientX, e.clientY);
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY, overId } : d));
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      const overId = zoneAt(e.clientX, e.clientY);
      if (!overId) return;
      const opt = config.options.find((o) => o.id === overId);
      if (opt) pick(opt);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!drag]);

  if (config.mode === "recovery") {
    return (
      <div className="usbdev usbdev--recovery">
        <div className="usbdev-panel">
          <div className="usbdev-kicker">{t("usb.momentLater")}</div>
          {config.situationLine && <p className="usbdev-situation">{config.situationLine}</p>}
          <ChoiceList>
            {config.options.map((opt) => (
              <ChoiceCard
                key={opt.id}
                category={opt.key === "reported" ? "verify" : "comply"}
                reply={opt.label}
                showRiskLabel={false}
                onClick={() => pick(opt)}
              />
            ))}
          </ChoiceList>
        </div>
      </div>
    );
  }

  // Stage 1: ONLY the sticky note is visible — a deterministic click (same
  // "prove you engaged" pattern as every power-on gate in this app) reveals
  // the USB + the drag targets, instead of dumping note+USB+3 text choices
  // on screen all at once.
  if (stage === "note") {
    return (
      <div className="usbdev usbdev--decision usbdev--note-stage">
        <button
          type="button"
          className="usbdev-note usbdev-note--standalone"
          onClick={() => {
            audio.click();
            setStage("dnd");
          }}
        >
          <span className="usbdev-note-pin" aria-hidden />
          <p>{config.noteText}</p>
          {config.senderLine && <p className="usbdev-note-sender">{config.senderLine}</p>}
          <span className="usbdev-note-tap">{t("usb.tapNote")}</span>
        </button>
      </div>
    );
  }

  const draggingOpt = drag ? true : false;

  return (
    <div className="usbdev usbdev--decision usbdev--dnd-stage">
      <div className="usbdev-panel usbdev-panel--dnd">
        <div className="usbdev-dnd-head">
          <div className="usbdev-dnd-note usbdev-note">
            <span className="usbdev-note-pin" aria-hidden />
            <p>{config.noteText}</p>
            {config.senderLine && <p className="usbdev-note-sender">{config.senderLine}</p>}
          </div>
          <p className="usbdev-dnd-prompt">{t("usb.dragPrompt")}</p>
        </div>

        {!draggingOpt ? (
          <div
            className="usbdev-usb usbdev-usb--draggable"
            onPointerDown={(e) => {
              (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
              startUsbDrag(e.clientX, e.clientY);
            }}
          >
            <svg viewBox="0 0 48 28" width="56" height="32" className="usbdev-usb-svg">
              <rect x="2" y="6" width="30" height="16" rx="2.5" fill="#c8ccd2" />
              <rect x="30" y="10" width="14" height="8" rx="1.5" fill="#9aa1ab" />
              <rect x="6" y="9" width="22" height="10" rx="1" fill="#e8eaee" />
            </svg>
            <span className="usbdev-usb-label">{config.usbLabel ?? t("usb.drive")}</span>
          </div>
        ) : (
          <div className="usbdev-usb usbdev-usb--placeholder" aria-hidden />
        )}

        <div className="usbdev-dnd-zones">
          {config.options.map((opt) => (
            <div
              key={opt.id}
              ref={(el) => {
                zoneRefs.current[opt.id] = el;
              }}
              className={`usbdev-dnd-zone ${drag?.overId === opt.id ? "over" : ""}`}
            >
              <span className="usbdev-dnd-zone-ico" aria-hidden>
                {opt.id === "insert_personal" ? <UsbLaptopIcon /> : <UsbTargetIcon optKey={opt.key} />}
              </span>
              <span className="usbdev-dnd-zone-label">{opt.label}</span>
            </div>
          ))}
        </div>
      </div>

      {drag && (
        <div className="usbdev-drag-ghost" style={{ left: drag.x, top: drag.y }} aria-hidden>
          <svg viewBox="0 0 48 28" width="48" height="27">
            <rect x="2" y="6" width="30" height="16" rx="2.5" fill="#c8ccd2" />
            <rect x="30" y="10" width="14" height="8" rx="1.5" fill="#9aa1ab" />
            <rect x="6" y="9" width="22" height="10" rx="1" fill="#e8eaee" />
          </svg>
        </div>
      )}
    </div>
  );
}
